import { Injectable, inject } from '@angular/core';
import { HandleError } from '@core/errors/store/actions';
import { ProcessesClient } from '@core/services/api-clients';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  LoadProcessEntityAssignmenetsSuccess,
  LoadProcessEntityAssignments,
  LoadProcessHistory,
  LoadProcessHistorySuccess,
  LoadProcessStateWithTransitions,
  LoadProcessStateWithTransitionsSuccess,
  ProcessActionsTypes,
  LoadProcessStatesForEntity,
  LoadProcessStatesForEntitySuccess,
} from './actions';
import { getProcessAssignments, getProcessStates, getProcessStatesByEntity } from './selectors';

@Injectable()
export class ProcessEffects {
  private _actions$ = inject(Actions);
  private _store = inject(Store);
  private _processesClient = inject(ProcessesClient);

  loadProcessAssignment$ = createEffect(() =>
    this._actions$.pipe(
      ofType<LoadProcessEntityAssignments>(ProcessActionsTypes.LoadProcessEntityAssignments),
      map((action) => action.payload),
      withLatestFrom(this._store.select(getProcessAssignments)),
      switchMap(([payload, processAssignments]) => {
        if (processAssignments && processAssignments[payload.entityId]) {
          return EMPTY;
        } else {
          return this._processesClient
            .getProcessAssignmentsForEntity(payload.entityId)
            .pipe(
              map(
                (x) =>
                  new LoadProcessEntityAssignmenetsSuccess({ entityId: payload.entityId, procesEntitytAssignments: x }),
              ),
            );
        }
      }),
    ),
  );

  loadProcessSteteWithTransitions$ = createEffect(() =>
    this._actions$.pipe(
      ofType<LoadProcessStateWithTransitions>(ProcessActionsTypes.LoadProcessStateWithTransitions),
      map((action) => action.payload.sourceStateId),
      withLatestFrom(this._store.select(getProcessStates)),
      mergeMap(([processStateId, processStates]) => {
        if (processStates && processStates[processStateId]) {
          return EMPTY;
        } else {
          return this._processesClient.getProcessStateWithTransitions(processStateId).pipe(
            catchError((error) => {
              this._store.dispatch(new HandleError({ error }));
              return EMPTY;
            }),
            mergeMap((processStateDto) => {
              return of(
                new LoadProcessStateWithTransitionsSuccess({
                  sourceStateId: processStateId,
                  processState: processStateDto,
                }),
              );
            }),
          );
        }
      }),
    ),
  );

  loadProcessHistory$ = createEffect(() =>
    this._actions$.pipe(
      ofType<LoadProcessHistory>(ProcessActionsTypes.LoadProcessHistory),
      map((action) => action.payload),
      switchMap((payload) => {
        return this._processesClient.getProcessHistory(payload.processAssignmentId, payload.recordId).pipe(
          map(
            (x) =>
              new LoadProcessHistorySuccess({
                processAssignmentId: payload.processAssignmentId,
                processHistoryItems: x,
              }),
          ),
        );
      }),
    ),
    );

    loadProcessStatesForEntity$ = createEffect(() =>
        this._actions$.pipe(
            ofType<LoadProcessStatesForEntity>(
                ProcessActionsTypes.LoadProcessStatesForEntity
            ),
            map(action => action.payload),
            withLatestFrom(this._store.select(getProcessStatesByEntity)),
            switchMap(([payload, processStates]) => {

                if (processStates && processStates[payload.entityId]) {
                    return EMPTY;
                }

                return this._processesClient
                    .getProcessStatesForEntity(payload.entityId)
                    .pipe(
                        map(states =>
                            new LoadProcessStatesForEntitySuccess({
                                entityId: payload.entityId,
                                processStates: states,
                            }),
                        ),
                        catchError(error => {
                            this._store.dispatch(new HandleError({ error }));
                            return EMPTY;
                        }),
                    );
            }),
        ),
    );

  constructor() {}
}
